/**
 * Версии стиля в интерфейсе — один словарь на орган «Настройки», диалог «Запустить
 * генерацию», редактор стилей, экран сравнения, строку бэклога и шапку черновика
 * (docs/release-2026-09-19.md §5).
 *
 * Вынесено сюда по той же причине, что и `format.ts`: подпись версии и правило «какие версии
 * можно выбрать» нужны пяти экранам, и пять копий разошлись бы на первой правке — один и тот
 * же стиль назывался бы в шапке «v5», в диалоге «v5 (релиз)», а в бэклоге никак.
 */

import {
  type RenderComparison,
  type RenderStyle,
  type StyleDefault,
  StyleDefaultSchema,
  type StyleVersion,
  type StyleVersionsResponse,
  StyleVersionsResponseSchema,
} from '../api/types.ts';

/**
 * Ответ `GET /api/v1/styles` разбирается схемой, а не приводится типом: список версий рисуют
 * диалоги на каждом экране, и чужой ответ (старый бэкенд, заглушка прокси) обязан стать
 * словом «не загрузились», а не исключением посреди «Запустить генерацию».
 */
export function parseStyleVersions(data: unknown): StyleVersionsResponse | null {
  if (data === undefined) return null;
  const r = StyleVersionsResponseSchema.safeParse(data);
  return r.success ? r.data : null;
}

/** То же для `GET /api/v1/style-default`: орган шапки живёт на каждом экране. */
export function parseStyleDefault(data: unknown): StyleDefault | null {
  if (data === undefined) return null;
  const r = StyleDefaultSchema.safeParse(data);
  return r.success ? r.data : null;
}

/**
 * Версии, которые человек может выбрать: скрытые — нет, ими писать нельзя (бэкенд ответит
 * `style_archived`). Исключение одно — `keep`: версия, которая УЖЕ стоит (умолчание, открытая
 * в редакторе). Спрятать её значило бы нарисовать ряд, в котором текущее состояние не нажато
 * ничем, — то есть соврать о нём.
 *
 * Релиз — первым в любом ряду. Сервер отдаёт его первым и сам (`StyleVersionsResponseSchema`),
 * но порядок чипов — обещание экрана («релиз, затем копии»), а не совпадение с чужой
 * сортировкой; копии остаются в порядке ответа — свежие первыми.
 */
export function pickableStyles(
  versions: readonly StyleVersion[],
  keep: string | null,
): readonly StyleVersion[] {
  const shown = versions.filter((v) => !v.archived || v.id === keep);
  return [
    ...shown.filter((v) => v.kind === 'release'),
    ...shown.filter((v) => v.kind !== 'release'),
  ];
}

/**
 * Имя версии с её видом: «v5 (релиз)». Релиз без `knowledge/style.json` называется «релиз», и
 * «релиз (релиз)» было бы опиской, а не подписью.
 */
export function styleWithKind(v: Pick<StyleVersion, 'kind' | 'name'>): string {
  if (v.kind !== 'release' || v.name === 'релиз') return v.name;
  return `${v.name} (релиз)`;
}

/**
 * Мета стиля у поста (§5.4, §5.5): «A · v5» / «B · v6» у пары сравнения, «стиль v6» у копии,
 * и НИЧЕГО у релиза. Релиз — то, чем канал пишет всегда, и подпись у каждой строки бэклога
 * «стиль v5» была бы шумом ровно там, где отличие должно бросаться в глаза.
 *
 * У пары буква стоит всегда, даже без ярлыка версии (пост старше стилей как данных такой
 * парой быть не может, но форма ответа это допускает): «A» без имени честнее, чем ничего.
 */
export function styleMetaOf(
  style: RenderStyle | null,
  comparison: RenderComparison | null,
): string | null {
  if (comparison !== null) {
    const side = comparison.side === 'a' ? 'A' : 'B';
    return style === null ? side : `${side} · ${style.name}`;
  }
  if (style !== null && style.kind === 'custom') return `стиль ${style.name}`;
  return null;
}
